import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
} from "firebase/auth";
import { auth } from "./firebase";
import {
  fetchUserData,
  fetchUserCountries,
  CreateGoogleUserDoc,
} from "./authApi";
import { clearAuthCache } from "./authUtils";
import { isDashmoteStaffEmail } from "./isDashmoteStaffEmail";
import { extractClientId } from "./extractClientId";
import { resetActivityTimestamp } from "./useIdleTimer";

const googleProvider = new GoogleAuthProvider();

// Translate the Firebase popup error codes the user can act on into a message.
const googleLoginErrorMessage = (error) => {
  switch (error?.code) {
    case "auth/popup-blocked":
      return "Popup blocked. Allow popups for this site and try again.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Login cancelled.";
    default:
      return error?.message || "Login failed! Please try again.";
  }
};

const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  isInitialized: false,
  signInPending: false,
  accountStatus: "idle",
  accountError: null,
  accountRequestId: null,
  profileRequestId: null,
  countriesRequestId: null,
  id_client: null,
  id_user: null,
  userData: null, // Store complete user data from API
  userDataFetchedThisSession: false, // Track if userData was fetched in current session (not persisted)
  countriesFetchedThisSession: false, // Track if client countries fetch has settled this session (not persisted)
  countries: [],
};

export const loginWithEmail = createAsyncThunk(
  "auth/loginWithEmail",
  async ({ email, password, message }, { rejectWithValue }) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      resetActivityTimestamp();
      if (message) {
        message.success({
          key: "auth",
          content: "Login Successfully!",
          duration: 3,
        });
      }
      return {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName,
        photoURL: userCredential.user.photoURL,
      };
    } catch (error) {
      console.error("Login failed:", error);
      if (message) {
        message.error({
          key: "auth",
          content: "Login failed! Please check your credentials.",
          duration: 3,
        });
      }
      return rejectWithValue(error.message);
    }
  },
);

export const fetchUserDataAction = createAsyncThunk(
  "auth/fetchUserData",
  async ({ withScope = true }, { rejectWithValue, getState }) => {
    try {
      const { auth: authState } = getState();

      if (!authState.isAuthenticated || !authState.user) {
        throw new Error("User not authenticated");
      }

      const userData = await fetchUserData(withScope);
      return userData;
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      return rejectWithValue(error.message);
    }
  },
);

export const fetchClientCountries = createAsyncThunk(
  "auth/fetchClientCountries",
  async (payload = {}, { getState, rejectWithValue }) => {
    const { auth } = getState();
    const { id_client, id_user: stateIdUser, userData, user } = auth;

    if (!id_client) {
      return rejectWithValue("No client ID available.");
    }

    const resolvedIdUser =
      payload.id_user ?? stateIdUser ?? userData?.id_user ?? user?.uid ?? null;

    if (!resolvedIdUser) {
      return rejectWithValue("No user ID available.");
    }

    const resolvedProductName = payload.product_name ?? "execution_view";

    try {
      const { regions } = await fetchUserCountries({
        id_client,
        id_user: resolvedIdUser,
        product_name: resolvedProductName,
      });

      return regions;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch client countries",
      );
    }
  },
);

export const loginWithGoogle = createAsyncThunk(
  "auth/loginWithGoogle",
  async ({ message }, { rejectWithValue }) => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const email = user.email;
      if (!isDashmoteStaffEmail(email)) {
        await signOut(auth); // the portal is staff-only, so a non-Dashmote account is signed straight back out
        message.error({
          key: "auth",
          content: "Login failed! Please use dashmote account!",
          duration: 3,
        });
        return rejectWithValue("Only @dashmote.com emails are allowed.");
      }
      // A deliberate sign-in starts a fresh idle window.
      resetActivityTimestamp();
      message.success({
        key: "auth",
        content: "Login Successfully!",
        duration: 3,
      });

      return {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
      };
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("Staff sign-in failed:", error?.code || "unknown");
      }
      return rejectWithValue(googleLoginErrorMessage(error));
    }
  },
);

export const logout = createAsyncThunk("auth/logout", async () => {
  try {
    await signOut(auth);
  } catch {
    // A transient signOut failure would otherwise leave the Firebase session
    // record behind. Retry once; if it still fails, fall through to the cache
    // clear and let the caller hard-navigate anyway.
    try {
      await signOut(auth);
    } catch {
      /* best effort — the finally clears the app cache regardless */
    }
  } finally {
    // Always clear the app-level cache, even when signOut failed, so a broken
    // logout can't strand a stale token for the next getAuthToken call.
    clearAuthCache();
  }
  return null;
});

export const startAuthListener = () => (dispatch) => {
  let initialized = false;
  return onAuthStateChanged(auth, (user) => {
    if (initialized && user) resetActivityTimestamp();
    initialized = true;
    dispatch(
      authSlice.actions.setUser(
        user
          ? {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL,
            }
          : null,
      ),
    );
  });
};

export const prepareStaffAccount = createAsyncThunk(
  "auth/prepareStaffAccount",
  async (_, { getState, rejectWithValue }) => {
    const user = getState().auth.user;
    if (!user) return rejectWithValue("Sign in before preparing your account.");
    try {
      try {
        const account = await fetchUserData(false);
        if (account?.id_user !== user.uid) {
          throw new Error(
            "The account response does not match the signed-in user.",
          );
        }
      } catch (error) {
        if (error.status !== 404) throw error;
        if (getState().auth.user?.uid !== user.uid) {
          throw new Error("The signed-in account changed.");
        }
        await CreateGoogleUserDoc({ email: user.email, id_user: user.uid });
      }
      return user.uid;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
  {
    condition: (_, { getState }) => getState().auth.accountStatus !== "loading",
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action) => {
      if (state.user?.uid !== action.payload?.uid) {
        return {
          ...initialState,
          user: action.payload,
          isInitialized: true,
          isAuthenticated: Boolean(action.payload),
          signInPending: state.signInPending,
          loading: state.signInPending,
        };
      }
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.isInitialized = true;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Clearing dataFetched makes the next render re-fetch the user's data.
    forceRefreshUserData: (state) => {
      if (state.user) {
        state.user.dataFetched = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginWithEmail.pending, (state) => {
        state.loading = true;
        state.signInPending = true;
        state.error = null;
      })
      .addCase(loginWithEmail.fulfilled, (state) => {
        state.loading = false;
        state.signInPending = false;
        state.error = null;
      })
      .addCase(loginWithEmail.rejected, (state, action) => {
        state.loading = false;
        state.signInPending = false;
        state.error = action.payload;
      })
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.signInPending = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state) => {
        state.loading = false;
        state.signInPending = false;
        state.error = null;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.signInPending = false;
        state.error = action.payload;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, () => {
        return { ...initialState, isInitialized: true };
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(prepareStaffAccount.pending, (state, action) => {
        state.accountStatus = "loading";
        state.accountError = null;
        state.accountRequestId = action.meta.requestId;
      })
      .addCase(prepareStaffAccount.fulfilled, (state, action) => {
        if (state.accountRequestId !== action.meta.requestId) return;
        state.accountStatus = "ready";
        state.accountRequestId = null;
      })
      .addCase(prepareStaffAccount.rejected, (state, action) => {
        if (state.accountRequestId !== action.meta.requestId) return;
        state.accountStatus = "error";
        state.accountError = action.payload;
        state.accountRequestId = null;
      })
      .addCase(fetchUserDataAction.pending, (state, action) => {
        state.profileRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDataAction.fulfilled, (state, action) => {
        if (state.profileRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.userData = action.payload; // Store complete user data
        state.id_user = action.payload.id_user;
        state.userDataFetchedThisSession = true; // Set session-specific flag (not persisted)

        // Admin is workspace-agnostic: no global selected workspace. id_client
        // resolves to the user's default workspace only to seed the user's own
        // client-countries fetch; screens that need a workspace (e.g. CASS) use
        // their own per-screen picker sourced from workspaceSlice.allWorkspaces.
        state.id_client = extractClientId(
          action.payload,
          action.payload.default_id_client,
        );
        state.error = null;

        // Mark user data as fetched to prevent duplicate requests
        if (state.user) {
          state.user.dataFetched = true;
        }
      })
      .addCase(fetchUserDataAction.rejected, (state, action) => {
        if (state.profileRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchClientCountries.pending, (state, action) => {
        state.countriesRequestId = action.meta.requestId;
        state.loading = true;
      })
      .addCase(fetchClientCountries.fulfilled, (state, action) => {
        if (state.countriesRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.countries = action.payload;
        state.countriesFetchedThisSession = true;
      })
      .addCase(fetchClientCountries.rejected, (state, action) => {
        if (state.countriesRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.payload;
        // Mark settled even on failure so dependent loaders fall back to their
        // real empty/error state instead of waiting on a fetch that won't retry.
        state.countriesFetchedThisSession = true;
      });
  },
});

export const { setUser, clearError, forceRefreshUserData } = authSlice.actions;
export default authSlice.reducer;
